import { StandardProps } from "../componentMap";
import { useVariant } from "../../hooks/useVariant/useVariant";
import { getPriceOrRange } from "@JohnLewisPartnership/jl-design-system/dist/components/price/helpers/getPriceOrRange";
import { useSeo } from "../../hooks/useSeo/useSeo";
import { ProductStatusBoxUI } from "./ProductStatusBoxUI";
import { getColourGroupForVariant } from "../../utils/product/getColourGroupForVariant";
import { getPriceValueOrRange, handleCTAClickToScroll } from "./utils";
import { useShowStoreStockButton } from "../../hooks/useShowStoreStockButton/useShowStoreStockButton";
import { isFutureRelease } from "../../utils/variant/isFutureRelease";
import { isAvailableToPreOrder } from "../../utils/variant/isAvailableToPreOrder";
import { PRODUCT_DESCRIPTION_ACCORDION_INSTANCE_NAME } from "@/components/ProductAccordion/components/ProductDescriptionAccordion/ProductDescriptionAccordionUI.tsx";
import { getNotifiableEventType } from "@/components/__shared__/NotAvailable/NotAvailable.tsx";
import { ensureIsActiveVariant } from "@/utils/product/ensureIsActive.ts";
import { isMultiVariants } from "@/utils/product/isMultiVariants.ts";

const checkInStoreStockSelector = '[data-testid="product:tools:store-stock"]';

type ProductStatusBoxProps = Pick<
  StandardProps,
  "product" | "configProps" | "runtimeConfig"
>;

export const ProductStatusBox = ({
  product,
  configProps,
  runtimeConfig: {
    storeStockApiKey,
    stockApiKey,
    storeStockModalApiUrl,
    stockApiBaseUrl,
  },
}: ProductStatusBoxProps) => {
  const { variant, isSelected } = useVariant(product);
  const { heading } = useSeo(product);
  const activeVariant = ensureIsActiveVariant(variant);
  const notifiableEventType =
    activeVariant !== null
      ? getNotifiableEventType(activeVariant.customerNotifiableEvents)
      : undefined;

  let showStoreStockButton = useShowStoreStockButton({
    storeStockApiKey,
    stockApiKey,
    storeStockModalApiUrl,
    stockApiBaseUrl,
    displayCode: variant.displayCode,
    skuId: variant.aliases.skuId,
    isFutureRelease: isFutureRelease(variant),
    isPreOrderable: isAvailableToPreOrder(variant),
  });
  showStoreStockButton =
    configProps?.showStoreStockButton === true
      ? showStoreStockButton
      : "disabled";
  const isOOSHowToGetIt = configProps?.isOOSHowToGetIt === true;

  const statusMessage = String(configProps?.statusMessage || "");
  const showPrice = configProps?.showPrice === true;

  const { variantPriceRange } = product;
  const hasMultiVariants = isMultiVariants(product);
  const hideTitle = configProps?.hideTitle === true;

  let priceRange;

  if (showPrice) {
    if (configProps?.useVariantGroups && !isSelected) {
      const variantGroup = getColourGroupForVariant(
        product.variantGroups,
        variant.id,
      );
      const variantGroupPriceRange = variantGroup?.variantPriceRange;

      priceRange = variantGroupPriceRange || variantPriceRange;
    } else {
      priceRange = isSelected ? variant.price : variantPriceRange;
    }
  }

  const formattedPrice = getPriceOrRange(
    getPriceValueOrRange(priceRange?.display),
  );

  if (!statusMessage) {
    return null;
  }

  const showHowToGetIt =
    hasMultiVariants || (isOOSHowToGetIt && notifiableEventType !== undefined);
  const hideViewDescriptionLink = false;

  const handleAccordionDescriptionCTAClick = () => {
    const target = showHowToGetIt
      ? '[data-component="OutOfStockActions"]'
      : "#accordion-description";

    if (!showHowToGetIt) {
      document.body.dispatchEvent(
        new CustomEvent(
          `jlds:accordion:${PRODUCT_DESCRIPTION_ACCORDION_INSTANCE_NAME}`,
          {
            detail: { action: "open" },
          },
        ),
      );
    }

    handleCTAClickToScroll(target, {
      block: "start",
    })();
  };

  const handleViewImagesButtonClick = () => {
    const carouselGalleryScrollEvent = new CustomEvent(
      "carousel-gallery-scroll-into-view",
    );
    document.dispatchEvent(carouselGalleryScrollEvent);
  };

  return (
    <ProductStatusBoxUI
      showHowToGetIt={showHowToGetIt}
      image={variant.images.primary}
      statusMessage={statusMessage}
      price={formattedPrice}
      productType={product.__typename}
      showStoreStockButton={showStoreStockButton === "visible"}
      title={heading}
      handleImageClick={handleViewImagesButtonClick}
      handleDescriptionCTA={handleAccordionDescriptionCTAClick}
      handleCheckInStoreStockClick={handleCTAClickToScroll(
        checkInStoreStockSelector,
      )}
      hideTitle={hideTitle}
      hideViewDescriptionLink={hideViewDescriptionLink}
    />
  );
};
